import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { Modal } from '@spirit-testing/component-library';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, take } from 'rxjs/operators';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { PersonOption, PersonSelectComponent } from '../../components/person-select/person-select.component';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { SectionTitleComponent } from '../../components/section-title/section-title.component';
import { RepoAnbindungComponent } from './repo-anbindung/repo-anbindung.component';
import { UmgebungPillComponent } from '../../components/umgebung-pill/umgebung-pill.component';
import { RunnerTargetSelectComponent } from '../../components/runner-target-select/runner-target-select.component';
import { AccountService } from '../../account/account.service';
import { ProjectMemberService } from '../../services/project-member.service';
import { TestCaseService } from '../../services/test-case.service';
import { TestPlanService } from '../../services/test-plan.service';
import { pickCurrentRelease, ReleaseService } from '../../services/release.service';
import { RepositoryBindingService } from '../../services/repository-binding.service';
import { runTargetBody, TestRunService } from '../../services/test-run.service';
import type { RunTarget } from '../../services/execution-job.service';
import { TestdatenblattService } from '../../services/testdatenblatt.service';
import { TestCase } from '../../models/test-case.model';
import { TestPlan } from '../../models/test-plan.model';
import { Release } from '../../models/release.model';
import { Testdatenblatt } from '../../models/testdatenblatt.model';
import { ProjectMemberDTO } from '../../models/project-member.model';
import { RepositoryBinding, RepositoryPipeline } from '../../models/repository-binding.model';
import {
  CreateTestRunRequest,
  DATA_SHEET_TARGET_ALL,
  DataSheetAssignment,
  RepositoryPipelineRef,
  RUN_KINDS,
  RunKind,
  TEST_RUN_ENVIRONMENTS,
  TestRunSettings,
  TestRunSourceType,
  UpdateTestRunRequest,
} from '../../models/test-run.model';
import { reviewerOptionsFromMembers } from '../../shared/project-member-options';
import { prefillOwnerWithCurrentUser } from '../../shared/forms/owner-prefill';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { createdRunRoute } from '../../shared/test-run-routes';
import { findRouteParam } from '../../shared/route-params';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';

/** Verzögerung der serverseitigen Testfall-Suche; hält die Tastatureingabe flüssig. */
const SEARCH_DEBOUNCE_MS = 250;

/** Bei diesen Antworten nennt der Text des Backends den Grund (Rechner aus, Fall ohne TCDL, …). */
const STATUSES_WITH_MESSAGE: ReadonlySet<number> = new Set([400, 404, 409, 422]);

function backendMessage(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse) || !STATUSES_WITH_MESSAGE.has(err.status)) return null;
  const message: unknown = err.error?.error;
  return typeof message === 'string' && message.trim() ? message : null;
}

function setWithout(source: ReadonlySet<number>, value: number): ReadonlySet<number> {
  const next = new Set(source);
  next.delete(value);
  return next;
}

/** Ein Eintrag der Ausführungsreihenfolge — je nach Quelle ein Plan oder ein Testfall. */
export interface SelectionItem {
  id: string;
  kind: 'PLAN' | 'CASE';
  key: string;
  label: string;
  /** Anzahl Testfälle eines Plans; für einen einzelnen Testfall `null`. */
  caseCount: number | null;
}

interface TestlaufNeuFormShape {
  name: FormControl<string>;
  releaseId: FormControl<string>;
  environment: FormControl<string>;
  owner: FormControl<string>;
  tags: FormControl<string>;
  note: FormControl<string>;
}

/**
 * Testlauf anlegen (NEX-1036).
 *
 * <p>Zwei Achsen: die <em>Art</em> (manuell/automatisiert) steht in der Sidebar,
 * die <em>Quelle</em> (Testplan/Testfälle/Repository) in der Hauptspalte. Das
 * Backend kennt je Lauf genau eine Quelle, deshalb zeigt die Auswahl-Übersicht
 * ausschließlich die Einträge der aktiven Quelle — eine gemischte Reihenfolge
 * über Pläne <em>und</em> Testfälle ließe sich nicht abschicken.
 *
 * <p>Formularfelder laufen über Reactive Forms (`app-person-select` ist ein
 * ControlValueAccessor), Auswahl und Reihenfolge über Signals.
 *
 * <p>Unter `test-runs/:runId/edit` dieselbe Seite als Bearbeiten-Modus (NEX-1100):
 * änderbar sind nur die Kopfdaten Name, Umgebung, Verantwortlicher, Tags und Notiz.
 * Art, Release und Zusammenstellung bleiben sichtbar, aber gesperrt — das Backend
 * weist Änderungen daran ab.
 */
@Component({
  selector: 'app-testlauf-neu',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatIcon,
    MatFormField,
    MatHint,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    DatePipe,
    Modal,
    AvatarComponent,
    PersonSelectComponent,
    ProjectNavComponent,
    RepoAnbindungComponent,
    RunnerTargetSelectComponent,
    SectionTitleComponent,
    UmgebungPillComponent,
  ],
  templateUrl: './testlauf-neu.component.html',
  styleUrl: './testlauf-neu.component.scss',
})
export class TestlaufNeuComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly testRunService = inject(TestRunService);
  private readonly repositoryBindingService = inject(RepositoryBindingService);
  private readonly testPlanService = inject(TestPlanService);
  private readonly releaseService = inject(ReleaseService);
  private readonly testCaseService = inject(TestCaseService);
  private readonly testdatenblattService = inject(TestdatenblattService);
  private readonly projectMemberService = inject(ProjectMemberService);
  private readonly accountService = inject(AccountService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  readonly projectId: string;
  /** Der bearbeitete Lauf (`test-runs/:runId/edit`); `null` beim Anlegen. */
  private readonly runId = this.findParam('runId');
  readonly isEdit = this.runId !== null;
  /** Der geladene Stand des bearbeiteten Laufs; `null`, solange er lädt oder beim Anlegen. */
  readonly settings = signal<TestRunSettings | null>(null);
  readonly runLoadError = signal(false);

  readonly fieldLengths = FIELD_LENGTHS;
  readonly targetAll = DATA_SHEET_TARGET_ALL;

  readonly art = signal<RunKind>('MANUAL');
  readonly quelle = signal<TestRunSourceType>('PLAN');
  /** Die als gewählt markierte Kachel; ein Lauf ohne Quelle (Import) und ein noch ladender Lauf zeigen keine. */
  readonly activeSourceTile = computed<TestRunSourceType | null>(() =>
    this.isEdit && !this.settings()?.sourceType ? null : this.quelle(),
  );
  /** Rechner oder Etikett eines automatisierten Laufs aus Plänen oder Testfällen (NEX-1750). */
  readonly target = signal<RunTarget | null>(null);
  readonly showTarget = computed(() => !this.isEdit && this.art() === 'AUTOMATED' && this.quelle() !== 'REPO');
  /** Plan- und Testfall-Läufe starten auf einem Rechner; „Pipeline auslösen" gilt nur für Repository. */
  readonly submitLabelKey = computed(() => `testRunNew.submit.${this.showTarget() ? 'RUNNER' : this.art()}`);

  readonly plans = signal<TestPlan[]>([]);
  readonly releases = signal<Release[]>([]);
  // Bei einem Ladefehler ist die Liste ebenfalls leer; der Hinweis gilt nur für eine erfolgreich geladene Liste.
  private readonly releasesLoaded = signal(false);
  readonly releasesEmpty = computed(() => this.releasesLoaded() && this.releases().length === 0);
  readonly cases = signal<TestCase[]>([]);
  readonly sheets = signal<Testdatenblatt[]>([]);
  private readonly members = signal<ProjectMemberDTO[]>([]);
  /**
   * Titel je Testfall-ID aus allen bisher geladenen Listen. Die Suche ersetzt
   * `cases`, aber ein aufgeklappter Plan muss seine Testfälle weiter benennen
   * können — deshalb wird der Titel gemerkt, statt ihn neu zu laden.
   */
  private readonly caseTitles = signal<Map<string, TestCase>>(new Map());

  readonly repositories = signal<RepositoryBinding[]>([]);
  readonly pipelines = signal<RepositoryPipeline[]>([]);
  /** Der Branch, von dem die Pipelines gelesen wurden; `null`, solange keine vorliegen. */
  readonly pipelinesBranch = signal<string | null>(null);
  readonly selectedRepositoryId = signal<string | null>(null);
  readonly selectedPipelineFile = signal<string | null>(null);
  readonly pipelinesLoading = signal(false);
  /** Der Fehlercode des Backends zur letzten Pipeline-Abfrage, `null` wenn sie ging. */
  readonly pipelinesErrorCode = signal<string | null>(null);
  readonly repositoriesError = signal(false);
  private readonly pipelineRequest$ = new Subject<string>();

  readonly selectedPlanIds = signal<string[]>([]);
  readonly selectedCaseIds = signal<string[]>([]);
  readonly expandedPlanIds = signal<ReadonlySet<string>>(new Set());

  readonly assignments = signal<DataSheetAssignment[]>([]);
  readonly newSheetId = signal('');
  readonly newSheetTarget = signal('');
  /**
   * Zeilen, deren Ziel automatisch auf „Gesamter Lauf" zurueckgefallen ist.
   * Auf einem langen Formular passiert das weit weg vom Blickfeld, deshalb
   * bekommt die betroffene Zeile einen Hinweis (Index in {@link assignments}).
   */
  private readonly retargetedRows = signal<ReadonlySet<number>>(new Set());

  readonly search = signal('');
  private readonly searchTerm$ = new Subject<string>();

  readonly loadError = signal(false);
  readonly saving = signal(false);
  /** Die angezeigte Fehlermeldung des letzten Speicherns, `null` ohne Fehler. */
  readonly saveError = signal<string | null>(null);
  readonly dirty = signal(false);
  readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();

  readonly currentUserName = signal('');
  private releaseChosenByUser = false;

  // Gesperrte Controls zählen nicht zur Gültigkeit: der Name existiert nur beim
  // Bearbeiten, das Release ist dort nicht mehr änderbar.
  readonly form: FormGroup<TestlaufNeuFormShape> = this.fb.group({
    name: this.fb.control<string>({ value: '', disabled: !this.isEdit }, [
      Validators.required,
      Validators.maxLength(FIELD_LENGTHS.NAME_MAX),
    ]),
    releaseId: this.fb.control<string>({ value: '', disabled: this.isEdit }, [Validators.required]),
    environment: this.fb.control<string>('STAGING', [Validators.required]),
    owner: this.fb.control<string>('', [Validators.required]),
    tags: this.fb.control<string>('', [Validators.maxLength(FIELD_LENGTHS.NAME_MAX)]),
    note: this.fb.control<string>('', [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)]),
  });

  readonly artOptions: readonly RunKind[] = RUN_KINDS;

  private readonly listedEnvironments: ReadonlySet<string> = new Set(TEST_RUN_ENVIRONMENTS);

  /**
   * Pipeline-Läufe tragen den Branch, Importe einen Freitext als Umgebung. Der geladene
   * Wert bleibt als eigene Option wählbar, sonst müsste er beim Speichern überschrieben werden.
   */
  readonly environmentOptions = computed<string[]>(() => {
    const loaded = this.settings()?.environment;
    return loaded && !this.listedEnvironments.has(loaded)
      ? [...TEST_RUN_ENVIRONMENTS, loaded]
      : [...TEST_RUN_ENVIRONMENTS];
  });

  isListedEnvironment(environment: string): boolean {
    return this.listedEnvironments.has(environment);
  }

  /** Läufe aus der Pipeline-Beobachtung haben keinen Verantwortlichen; das Bearbeiten erzwingt keinen. */
  readonly ownerRequired = computed(() => !(this.isEdit && this.settings()?.owner === null));

  /**
   * Die Gueltigkeit eines Reactive Form ist kein Signal — ohne diese Bruecke
   * bliebe `canSubmit` auf dem Stand des ersten Renderings stehen.
   */
  private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });

  readonly personOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.members()));

  /** Repository ist laut Design nur bei automatisierter Ausführung eine Quelle. */
  readonly sourceTiles = computed<TestRunSourceType[]>(() =>
    this.art() === 'AUTOMATED' ? ['PLAN', 'CASES', 'REPO'] : ['PLAN', 'CASES'],
  );

  readonly selection = computed<SelectionItem[]>(() => {
    if (this.quelle() === 'REPO') return [];
    if (this.quelle() === 'PLAN') {
      const byId = new Map(this.plans().map((p) => [p.id, p]));
      return this.selectedPlanIds()
        .map((id) => byId.get(id))
        .filter((p): p is TestPlan => !!p)
        .map((p) => ({
          id: p.id,
          kind: 'PLAN' as const,
          key: p.publicId ?? p.id,
          label: p.title,
          caseCount: p.testCaseIds.length,
        }));
    }
    const byId = this.caseTitles();
    return this.selectedCaseIds()
      .map((id) => byId.get(id))
      .filter((c): c is TestCase => !!c)
      .map((c) => ({ id: c.id, kind: 'CASE' as const, key: c.key, label: c.title, caseCount: null }));
  });

  readonly selectedPlanCount = computed(() => (this.quelle() === 'PLAN' ? this.selection().length : 0));
  readonly selectedCaseCount = computed(() => (this.quelle() === 'CASES' ? this.selection().length : 0));

  readonly totalCaseCount = computed(() => this.selection().reduce((sum, item) => sum + (item.caseCount ?? 1), 0));

  /** Aus einer Plan-Quelle entsteht ein Lauf pro Plan, aus allen anderen genau einer. */
  readonly runCount = computed(() => {
    if (this.quelle() === 'REPO') return this.repoSourceComplete() ? 1 : 0;
    if (this.quelle() === 'PLAN') return this.selection().length;
    return this.selection().length > 0 ? 1 : 0;
  });

  readonly targetOptions = computed<{ id: string; label: string }[]>(() => [
    { id: DATA_SHEET_TARGET_ALL, label: '' },
    ...this.selection().map((item) => ({ id: item.id, label: `${item.key} · ${item.label}` })),
  ]);

  readonly wholeRunSheets = computed(() => this.sheetsForTarget(DATA_SHEET_TARGET_ALL));

  readonly canAddAssignment = computed(() => !!this.newSheetId() && !!this.newSheetTarget());

  /** Die Repository-Quelle ist vollstaendig, sobald Repository und Pipeline stehen. */
  readonly repoSourceComplete = computed(() => !!this.selectedRepositoryId() && !!this.selectedPipelineFile());

  /**
   * Der Repository-Weg legt einen Lauf mit {@code sourceType = REPO} an (NEX-1106).
   * Automatisierte Läufe aus Plänen oder Testfällen brauchen ein Ziel (NEX-1750).
   */
  readonly canSubmit = computed(() => {
    if (this.saving() || this.formStatus() !== 'VALID') return false;
    if (this.isEdit) return this.settings() !== null;
    if (this.quelle() === 'REPO') return this.repoSourceComplete();
    return this.selection().length > 0 && (this.art() === 'MANUAL' || this.target() !== null);
  });

  /** Warum der Knopf gesperrt ist — sonst bliebe der deaktivierte Knopf unerklärt. */
  readonly submitHintKey = computed<string | null>(() => {
    if (this.canSubmit() || this.isEdit) return null;
    if (this.quelle() === 'REPO') return 'testRunNew.submitBlockedRepo';
    return this.showTarget() && this.selection().length > 0 && this.target() === null
      ? 'testRunNew.submitBlockedTarget'
      : null;
  });

  private readonly releaseIdValue = toSignal(this.form.controls.releaseId.valueChanges, {
    initialValue: this.form.controls.releaseId.value,
  });

  readonly summaryReleaseLabel = computed<string | null>(() => {
    const id = this.releaseIdValue();
    if (!id) return null;
    return this.releases().find((r) => r.id === id)?.label ?? id;
  });

  /** Die Zusammenfassung nennt bei einer Repository-Quelle die gewählte Pipeline. */
  readonly summaryPipelineName = computed(() => {
    if (this.quelle() !== 'REPO') return null;
    const file = this.selectedPipelineFile();
    return this.pipelines().find((pipeline) => pipeline.file === file)?.name ?? null;
  });

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.projectId = this.findParam('projectId') ?? '';
    this.wireSearch();
    this.wirePipelines();
    if (this.isEdit) this.lockHeaderFields();
    this.reload();

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.form.dirty) this.dirty.set(true);
    });
  }

  reload(): void {
    if (!this.projectId) return;
    this.loadError.set(false);
    this.loadPlans();
    this.loadReleases();
    this.loadCases('');
    this.loadSheets();
    this.loadMembers();
    this.loadRepositories();
    if (this.runId) this.loadSettings(this.runId);
  }

  private loadSettings(runId: string): void {
    this.runLoadError.set(false);
    this.testRunService
      .getSettings(this.projectId, runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => this.applySettings(run),
        error: () => this.runLoadError.set(true),
      });
  }

  /** Bis der Lauf geladen ist, würde das Vorbefüllen Eingaben ohne Meldung überschreiben. */
  private lockHeaderFields(): void {
    this.headerControls().forEach((control) => control.disable({ emitEvent: false }));
  }

  private headerControls(): AbstractControl[] {
    const { name, environment, owner, tags, note } = this.form.controls;
    return [name, environment, owner, tags, note];
  }

  /** Füllt das Formular ohne Nutzereingabe — das Vorbefüllen allein darf nicht dirty machen. */
  private applySettings(run: TestRunSettings): void {
    this.settings.set(run);
    this.art.set(run.kind);
    if (run.sourceType) this.quelle.set(run.sourceType);
    this.selectedPlanIds.set(run.planIds);
    this.selectedCaseIds.set(run.caseIds);
    this.assignments.set(run.dataSheets);
    this.form.patchValue({
      name: run.name,
      releaseId: run.releaseId ?? '',
      environment: run.environment ?? '',
      owner: run.owner ?? '',
      tags: run.tags.join(', '),
      note: run.note ?? '',
    });
    if (run.owner === null) this.form.controls.owner.removeValidators(Validators.required);
    this.headerControls().forEach((control) => control.enable());
    const repo = run.repositoryPipeline;
    if (repo) {
      this.selectedRepositoryId.set(repo.repositoryId);
      this.selectedPipelineFile.set(repo.pipeline);
      this.pipelinesLoading.set(true);
      this.pipelineRequest$.next(repo.repositoryId);
    }
  }

  /**
   * Scheitert die Liste, meldet das die Karte selbst. Der Seitenbanner spricht von
   * Testplaenen und Testfaellen — wer manuell anlegt, bekaeme sonst einen Fehler
   * fuer etwas, das er gar nicht benutzt.
   */
  private loadRepositories(): void {
    this.repositoriesError.set(false);
    this.repositoryBindingService
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (repositories) => this.repositories.set(repositories ?? []),
        error: () => {
          this.repositories.set([]);
          this.repositoriesError.set(true);
        },
      });
  }

  /**
   * `switchMap` statt eines Abonnements je Klick: wer schnell zwischen zwei
   * Repositories wechselt, bekaeme sonst die spaeter eintreffende Antwort des
   * zuerst gewaehlten zu sehen.
   */
  private wirePipelines(): void {
    this.pipelineRequest$
      .pipe(
        switchMap((repositoryId) =>
          this.repositoryBindingService.pipelines(this.projectId, repositoryId).pipe(
            catchError((err: HttpErrorResponse) => {
              // Das Backend unterscheidet fehlendes Token, unsichtbares Repository
              // und Rate-Limit; die Karte haengt daran verschiedene Hinweise.
              this.pipelinesErrorCode.set((err.error?.code as string | undefined) ?? 'GENERIC');
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((answer) => {
        this.pipelinesLoading.set(false);
        this.pipelinesBranch.set(answer?.branch ?? null);
        this.pipelines.set(answer?.pipelines ?? []);
      });
  }

  private loadPlans(): void {
    this.testPlanService
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (plans) => {
          this.plans.set(plans ?? []);
          this.applyDerivedRelease();
        },
        error: () => this.loadError.set(true),
      });
  }

  private loadReleases(): void {
    this.releaseService
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (releases) => {
          this.releases.set(releases ?? []);
          this.releasesLoaded.set(true);
          this.applyDerivedRelease();
        },
        error: () => {
          this.releases.set([]);
          this.snackBar.open(this.translate.instant('testRunNew.releasesLoadError'), undefined, { duration: 4000 });
        },
      });
  }

  // setValue statt Nutzereingabe: die Vorbelegung allein darf das Formular nicht dirty machen.
  private applyDerivedRelease(): void {
    if (this.isEdit || this.releaseChosenByUser) return;
    const target = this.planReleaseId() ?? pickCurrentRelease(this.releases())?.id;
    const control = this.form.controls.releaseId;
    if (target && control.value !== target) control.setValue(target);
  }

  private planReleaseId(): string | null {
    if (this.quelle() !== 'PLAN') return null;
    const selected = this.selection();
    if (selected.length !== 1) return null;
    const releaseId = this.plans().find((p) => p.id === selected[0].id)?.releaseId;
    return releaseId && this.releases().some((r) => r.id === releaseId) ? releaseId : null;
  }

  onReleaseSelectedByUser(): void {
    this.releaseChosenByUser = true;
  }

  private loadCases(query: string): void {
    this.testCaseService
      .list(this.projectId, query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (cases) => this.applyCases(cases ?? []),
        error: () => this.loadError.set(true),
      });
  }

  private loadSheets(): void {
    this.testdatenblattService
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (sheets) => this.sheets.set(sheets ?? []),
        error: () => this.loadError.set(true),
      });
  }

  private loadMembers(): void {
    this.projectMemberService
      .getMembers(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (members) => {
          this.members.set(members ?? []);
          if (!this.isEdit) this.prefillOwner();
        },
        error: () => this.members.set([]),
      });
  }

  private prefillOwner(): void {
    this.accountService
      .getProfile()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((profile) => {
        if (!profile) return;
        prefillOwnerWithCurrentUser(this.form.controls.owner, profile.id, this.personOptions());
        this.currentUserName.set(this.personOptions().find((o) => o.id === profile.id)?.displayName ?? profile.email);
      });
  }

  private wireSearch(): void {
    this.searchTerm$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap((term) =>
          this.testCaseService.list(this.projectId, term).pipe(catchError(() => of([] as TestCase[]))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((cases) => this.applyCases(cases));
  }

  private applyCases(cases: TestCase[]): void {
    this.cases.set(cases);
    this.caseTitles.update((known) => {
      const merged = new Map(known);
      for (const testCase of cases) merged.set(testCase.id, testCase);
      return merged;
    });
  }

  onSearchInput(value: string): void {
    this.search.set(value);
    this.searchTerm$.next(value);
  }

  // --- Art & Quelle ------------------------------------------------------

  setArt(art: RunKind): void {
    if (this.isEdit || this.art() === art) return;
    this.art.set(art);
    // Repository verschwindet mit der Automatisierung; sonst bliebe eine Quelle
    // aktiv, die es in der Kachelreihe gar nicht mehr gibt.
    if (art === 'MANUAL' && this.quelle() === 'REPO') this.quelle.set('PLAN');
    this.forgetHiddenTarget();
    this.applyDerivedRelease();
    this.dirty.set(true);
  }

  setQuelle(quelle: TestRunSourceType): void {
    if (this.isEdit || this.quelle() === quelle) return;
    this.quelle.set(quelle);
    this.forgetHiddenTarget();
    this.dropUnreachableAssignments();
    this.applyDerivedRelease();
    this.dirty.set(true);
  }

  onTargetChange(target: RunTarget | null): void {
    this.target.set(target);
  }

  /**
   * Die Auswahl verschwindet mit dem Feld und lädt beim Wiederkommen neu. Bis sie sich meldet,
   * darf kein altes Ziel den Knopf freigeben.
   */
  private forgetHiddenTarget(): void {
    if (!this.showTarget()) this.target.set(null);
  }

  // --- Repository & Pipeline ---------------------------------------------

  /**
   * Ein erneuter Klick auf dasselbe Repository laedt nur nach, wenn der letzte
   * Versuch fehlschlug — sonst bliebe dem Nutzer nach einem Fehler nur der Umweg
   * ueber ein anderes Repository und zurueck.
   */
  onRepositorySelected(repositoryId: string): void {
    if (this.isEdit || (this.selectedRepositoryId() === repositoryId && !this.pipelinesErrorCode())) return;
    this.selectedRepositoryId.set(repositoryId);
    // Die Pipelines des vorigen Repositories gelten hier nicht mehr; bleiben sie
    // stehen, waehlt der naechste Klick eine Pipeline aus dem falschen Repository.
    this.selectedPipelineFile.set(null);
    this.pipelines.set([]);
    this.pipelinesBranch.set(null);
    this.pipelinesErrorCode.set(null);
    this.pipelinesLoading.set(true);
    this.pipelineRequest$.next(repositoryId);
    this.dirty.set(true);
  }

  onPipelineSelected(file: string | null): void {
    if (this.isEdit) return;
    this.selectedPipelineFile.set(file);
    this.dirty.set(true);
  }

  // --- Auswahl -----------------------------------------------------------

  isPlanSelected(planId: string): boolean {
    return this.selectedPlanIds().includes(planId);
  }

  isCaseSelected(caseId: string): boolean {
    return this.selectedCaseIds().includes(caseId);
  }

  togglePlan(planId: string): void {
    if (this.isEdit) return;
    this.selectedPlanIds.update((ids) => (ids.includes(planId) ? ids.filter((id) => id !== planId) : [...ids, planId]));
    this.dropUnreachableAssignments();
    this.applyDerivedRelease();
    this.dirty.set(true);
  }

  toggleCase(caseId: string): void {
    if (this.isEdit) return;
    this.selectedCaseIds.update((ids) => (ids.includes(caseId) ? ids.filter((id) => id !== caseId) : [...ids, caseId]));
    this.dropUnreachableAssignments();
    this.dirty.set(true);
  }

  removeSelection(itemId: string): void {
    if (this.quelle() === 'PLAN') this.togglePlan(itemId);
    else this.toggleCase(itemId);
  }

  moveSelection(index: number, delta: number): void {
    if (this.isEdit) return;
    const target = index + delta;
    const ids = this.quelle() === 'PLAN' ? this.selectedPlanIds : this.selectedCaseIds;
    const current = ids();
    if (target < 0 || target >= current.length) return;
    const reordered = [...current];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    ids.set(reordered);
    this.dirty.set(true);
  }

  isExpanded(planId: string): boolean {
    return this.expandedPlanIds().has(planId);
  }

  togglePlanExpanded(planId: string): void {
    this.expandedPlanIds.update((open) => {
      const next = new Set(open);
      if (!next.delete(planId)) next.add(planId);
      return next;
    });
  }

  /** Testfälle eines Plans in Planreihenfolge; unbekannte Titel bleiben leer. */
  planCases(planId: string): { id: string; key: string; title: string }[] {
    const plan = this.plans().find((p) => p.id === planId);
    if (!plan) return [];
    const known = this.caseTitles();
    return plan.testCaseIds.map((id) => {
      const testCase = known.get(id);
      return { id, key: testCase?.key ?? '', title: testCase?.title ?? '' };
    });
  }

  // --- Testdatenblätter --------------------------------------------------

  sheetsForTarget(target: string): Testdatenblatt[] {
    const byId = new Map(this.sheets().map((s) => [s.id, s]));
    return this.assignments()
      .filter((a) => a.target === target)
      .map((a) => byId.get(a.id))
      .filter((s): s is Testdatenblatt => !!s);
  }

  sheetById(id: string): Testdatenblatt | undefined {
    return this.sheets().find((s) => s.id === id);
  }

  /** Ist das Ziel dieser Zeile automatisch auf den gesamten Lauf zurueckgefallen? */
  wasRetargeted(index: number): boolean {
    return this.retargetedRows().has(index);
  }

  addAssignment(): void {
    if (this.isEdit || !this.canAddAssignment()) return;
    // Neue Zeilen haengen sich hinten an, bestehende Indizes bleiben gueltig.
    this.assignments.update((list) => [...list, { id: this.newSheetId(), target: this.newSheetTarget() }]);
    this.newSheetId.set('');
    this.newSheetTarget.set('');
    this.dirty.set(true);
  }

  setAssignmentTarget(index: number, target: string): void {
    if (this.isEdit) return;
    this.assignments.update((list) => list.map((a, i) => (i === index ? { ...a, target } : a)));
    // Der Nutzer hat das Ziel selbst gesetzt; der Hinweis hat sich erledigt.
    this.retargetedRows.update((rows) => setWithout(rows, index));
    this.dirty.set(true);
  }

  removeAssignment(index: number): void {
    if (this.isEdit) return;
    this.assignments.update((list) => list.filter((_, i) => i !== index));
    // Alle Zeilen hinter der geloeschten ruecken auf — sonst haenge der Hinweis
    // hinterher an der falschen Zeile.
    this.retargetedRows.update(
      (rows) => new Set([...rows].filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))),
    );
    this.dirty.set(true);
  }

  /**
   * Zuordnungen auf ein Ziel, das nicht mehr in der Auswahl steht, würden vom
   * Backend als Zuordnung an ein fremdes Objekt gespeichert. Sie fallen auf den
   * gesamten Lauf zurück, statt still zu verschwinden.
   */
  private dropUnreachableAssignments(): void {
    const reachable = new Set([DATA_SHEET_TARGET_ALL, ...this.selection().map((i) => i.id)]);
    const moved: number[] = [];
    this.assignments.update((list) =>
      list.map((a, i) => {
        if (reachable.has(a.target)) return a;
        moved.push(i);
        return { ...a, target: DATA_SHEET_TARGET_ALL };
      }),
    );
    if (moved.length > 0) this.retargetedRows.update((rows) => new Set([...rows, ...moved]));
    if (this.newSheetTarget() && !reachable.has(this.newSheetTarget())) this.newSheetTarget.set('');
  }

  // --- Anlegen & Speichern ----------------------------------------------

  submit(): void {
    if (!this.canSubmit()) return;
    this.saving.set(true);
    this.saveError.set(null);
    if (this.runId) this.saveChanges(this.runId);
    else this.createRuns();
  }

  private createRuns(): void {
    this.testRunService
      .create(this.projectId, this.buildRequest())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          this.saving.set(false);
          this.dirty.set(false);
          const single = created.length === 1 ? created[0] : null;
          if (single && (single.nextAction === 'RUN_MANUAL' || single.nextAction === 'RUN_ON_RUNNER')) {
            void this.router.navigate(createdRunRoute(this.projectId, single));
            return;
          }
          // Der Knopf heisst „Pipeline ausloesen", das Anlegen allein stoesst sie aber
          // nicht an — ausgeloest wird ueber einen eigenen Aufruf (NEX-752). Ohne diesen
          // Hinweis wartet der Nutzer auf einen Lauf, der nie startet.
          if (single?.nextAction === 'TRIGGER_PIPELINE') {
            this.snackBar.open(this.translate.instant('testRunNew.repo.triggerHint'), undefined, {
              panelClass: 'snackbar-success',
            });
          }
          // Aus mehreren Plänen entstehen mehrere Rechner-Läufe; ohne Hinweis sähe die Übersicht unverändert aus.
          if (created.length > 1 && created.every((run) => run.nextAction === 'RUN_ON_RUNNER')) {
            this.snackBar.open(this.translate.instant('testRunNew.runsCreated', { count: created.length }), undefined, {
              panelClass: 'snackbar-success',
            });
          }
          this.goToOverview();
        },
        error: (err: unknown) => {
          this.saving.set(false);
          this.saveError.set(backendMessage(err) ?? this.translate.instant('testRunNew.saveError'));
        },
      });
  }

  private saveChanges(runId: string): void {
    this.testRunService
      .update(this.projectId, runId, this.buildUpdateRequest())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.dirty.set(false);
          this.snackBar.open(this.translate.instant('testRunNew.edit.saved'), undefined, {
            duration: 4000,
            panelClass: 'snackbar-success',
          });
          void this.router.navigate(['/projects', this.projectId, 'test-runs', runId, 'executions']);
        },
        error: (err: HttpErrorResponse) => {
          this.saving.set(false);
          this.saveError.set(this.updateErrorMessage(err));
        },
      });
  }

  /**
   * Ein 409 hat zwei Ursachen: Der Lauf ist gesperrt — der Server nennt den Grund
   * (abgeschlossen, abgebrochen, offene Übergabe) — oder jemand hat gleichzeitig
   * gespeichert (`STALE_VERSION`); dann hilft nur Neuladen. Ein 403 kommt vom
   * fehlenden Schreibrecht oder vom umgehängten Verantwortlichen — der Hinweis zum
   * Verantwortlichen passt nur, wenn er geändert wurde.
   */
  private updateErrorMessage(err: HttpErrorResponse): string {
    const reason: unknown = err.error?.error;
    const serverMessage = typeof reason === 'string' && reason ? reason : null;
    if (err.status === 409 && err.error?.code === 'STALE_VERSION') {
      return this.translate.instant('testRunNew.edit.staleError');
    }
    if (err.status === 403) {
      const ownerChanged = this.form.controls.owner.value !== (this.settings()?.owner ?? '');
      return this.translate.instant(ownerChanged ? 'testRunNew.edit.forbiddenError' : 'dashboard.forbiddenError');
    }
    if (err.status === 409) return serverMessage ?? this.translate.instant('testRunNew.edit.lockedError');
    if (err.status === 400 && serverMessage) return serverMessage;
    return this.translate.instant('testRunNew.edit.saveError');
  }

  /** Nur die Kopfdaten — Art, Release und Zusammenstellung weist das Backend ab. */
  private buildUpdateRequest(): UpdateTestRunRequest {
    const { name, environment, owner, note } = this.form.getRawValue();
    return { name: name.trim(), environment, owner: owner || null, tags: this.parsedTags(), note };
  }

  private buildRequest(): CreateTestRunRequest {
    const plan = this.quelle() === 'PLAN';
    return {
      kind: this.art(),
      sourceType: this.quelle(),
      releaseId: this.form.controls.releaseId.value,
      planIds: plan ? this.selection().map((i) => i.id) : [],
      caseIds: plan ? [] : this.selection().map((i) => i.id),
      repositoryPipeline: this.repositoryPipelineRef(),
      dataSheets: this.assignments(),
      environment: this.form.controls.environment.value,
      owner: this.form.controls.owner.value,
      tags: this.parsedTags(),
      note: this.form.controls.note.value,
      ...runTargetBody(this.showTarget() ? this.target() : null),
    };
  }

  /**
   * Das Backend nimmt die Referenz nur bei einer Repository-Quelle entgegen und
   * verlangt dort beide Werte; sonst bleibt sie weg.
   */
  private repositoryPipelineRef(): RepositoryPipelineRef | undefined {
    const repositoryId = this.selectedRepositoryId();
    const pipeline = this.selectedPipelineFile();
    if (this.quelle() !== 'REPO' || !repositoryId || !pipeline) return undefined;
    return { repositoryId, pipeline };
  }

  /** Das Backend erwartet eine Liste; das Design ein Freitextfeld „smoke, nightly". */
  private parsedTags(): string[] {
    return this.form.controls.tags.value
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => !!tag);
  }

  // --- Navigation & Verlassen --------------------------------------------

  goToOverview(): void {
    void this.router.navigate(['/projects', this.projectId, 'test-runs']);
  }

  hasUnsavedChanges(): boolean {
    return this.dirty() && !this.saving();
  }

  confirmDeactivate(): Observable<boolean> {
    if (!this.hasUnsavedChanges()) return of(true);
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}
